import { useMemo, type JSX } from "react";

import {
    formatDateWithTimezone,
    formatTimeAndTimezone,
} from "@impero-com/common/utils/formatting";
import { TFunction, useTranslation } from "@impero-com/common/utils/i18n";
import { assertNever } from "@impero-com/common/utils/obj";
import {
    Row,
    createColumnHelper,
    getCoreRowModel,
    useReactTable,
} from "@tanstack/react-table";
import { cva } from "class-variance-authority";
import moment, { Moment } from "moment-timezone";

import {
    ExistingControlResultPaginatedListDto as ControlResultPaginatedListDto,
    ExistingControlResult,
    DashboardFilters,
    IanaTimezoneView,
} from "api/generated/model";
import IconWave from "img/icon-wave-signal-square.svg";
import { LegacyRoute } from "legacyRoutes";
import {
    ActorRole,
    Event,
    EventType,
    computeEvent,
    extractExistingControlResults,
    convertEventTypeToExistingControlActivityStatus,
    computeRoleStep,
    localizeEvent,
} from "utils/controlResults/activityStatusUtils";
import {
    ActorName,
    getStepAssigneeOrSystemName,
    localizeActorName,
} from "utils/controlResults/actorName";
import {
    buildLookupContext,
    LookupContext,
} from "utils/controlResults/lookupContext";
import { useCurrentUserTimezone } from "utils/currentTimezone";

import { useBuildActivitiesUrl } from "../hooks/useBuildActivitiesUrl";
import { useDashboardFilteredData } from "../hooks/useDashboardFilteredData";

import * as theme from "./DashboardControlActivities.css";
import { Table } from "./shared/table";
import { TableWidgetLayout } from "./shared/tableWidgetLayout";

interface DashboardControlActivitiesProps {
    filters: DashboardFilters;
}

/**
 * Activities (aka control results) table that is meant to be used in the dashboard.
 */
export function DashboardControlActivities({
    filters,
}: DashboardControlActivitiesProps): JSX.Element {
    const { t } = useTranslation();
    const limit = 10; // Default limit for the number of activities to show
    const {
        filteredControlResultsDto: controlResultPaginatedListDto,
        dateRange,
    } = useDashboardFilteredData(filters);

    const buildActivitiesUrl = useBuildActivitiesUrl({
        dateRange,
        filteredControlProgramIds: filters.controlPrograms,
        filteredTags: filters.tags,
        filteredEntityIds: filters.entities,
        showArchivedActivities: filters.showArchivedActivities,
        showInactivePrograms: filters.showInactivePrograms,
    });

    const controlActivities = useMemo(() => {
        const now = moment();
        return buildControlActivities(
            controlResultPaginatedListDto,
            limit,
            now
        );
    }, [controlResultPaginatedListDto, limit]);

    return (
        <TableWidgetLayout
            headerTitle={t("DashboardControlActivities_Header")}
            headerSubtitle={filters.timePeriod}
            footer={
                dateRange ? <Footer href={buildActivitiesUrl(null)} /> : null // Omit the status key to show all activities
            }
        >
            <ControlActivitiesTable controlActivities={controlActivities} />
        </TableWidgetLayout>
    );
}

interface FooterProps {
    href: string;
}

function Footer({ href }: FooterProps) {
    const { t } = useTranslation();

    return (
        <a href={href} className={theme.footer}>
            {t("DashboardControlActivities_Footer_AllActivities")}
        </a>
    );
}

interface ControlActivitiesTableProps {
    controlActivities: Array<ControlActivity>;
}

function ControlActivitiesTable({
    controlActivities,
}: ControlActivitiesTableProps): JSX.Element {
    const { t } = useTranslation();

    const timezone: IanaTimezoneView = { name: useCurrentUserTimezone() };

    const columnHelper = createColumnHelper<ControlActivity>();

    const columnControl = columnHelper.accessor("title", {
        header: () => t("DashboardControlActivities_Name"),
        cell: (cellContext) => {
            const activity = cellContext.row.original;
            return <CellControl activity={activity} />;
        },
        enableSorting: false,
    });

    const columnActor = columnHelper.accessor("actor", {
        header: () => t("DashboardControlActivities_Responsible"),
        cell: (cellContext) => {
            const actor = cellContext.getValue();
            return <CellActor actor={actor} />;
        },
        enableSorting: false,
    });

    const columnEvent = columnHelper.accessor("event", {
        header: () => t("DashboardControlActivities_Event"),
        cell: (cellContext) => {
            const event = cellContext.getValue();
            return <CellEvent event={event} timezone={timezone} />;
        },
        enableSorting: false,
    });

    const table = useReactTable({
        data: controlActivities,
        getCoreRowModel: getCoreRowModel<ControlActivity>(),
        columns: [columnEvent, columnControl, columnActor],
    });

    return (
        <Table
            table={table}
            buildRowLinkUrl={buildRowLinkUrl}
            noDataFallback={{
                text: t("DashboardControlActivities_NoData"),
                svgIcon: IconWave,
            }}
        />
    );
}

/*
 * Plain control activity which represents a row in the table.
 */
interface ControlActivity {
    controlResultId: string;
    title: string;
    controlProgramName: string | null;
    actor: Actor;
    event: Event;
}

interface Actor {
    name: ActorName;
    role: ActorRole;
}

function buildControlActivities(
    dtoList: ControlResultPaginatedListDto,
    limit: number,
    now: Moment
): Array<ControlActivity> {
    const existingControlResults = extractExistingControlResults(dtoList);
    const lookupContext = buildLookupContext(dtoList);

    const allActivities = existingControlResults.map((cr) =>
        buildControlActivity(cr, lookupContext, now)
    );

    // Sort, so the most recent activities are on top.
    allActivities.sort((a, b) => {
        return b.event.datetime.unix() - a.event.datetime.unix();
    });

    return allActivities.slice(0, limit);
}

function buildControlActivity(
    existingControlResult: ExistingControlResult,
    lookupContext: LookupContext,
    now: Moment
): ControlActivity {
    const controlResultId = existingControlResult.id;

    // The program may have been deleted, in which case its id no longer resolves to a program in the lookupContext.
    // We still show the activity, falling back to the raw program id as the name.
    const controlProgram = lookupContext.controlProgramsById.get(
        existingControlResult.controlProgramId
    );
    const controlProgramName: string =
        controlProgram?.name ??
        existingControlResult.controlProgramId.toString();

    const event = computeEvent(existingControlResult, now);

    return {
        controlResultId,
        title: existingControlResult.title,
        controlProgramName,
        actor: computeActor(existingControlResult, lookupContext),
        event,
    };
}

function computeActor(
    existingControlResult: ExistingControlResult,
    lookupContext: LookupContext
): Actor {
    const { step, role } = computeRoleStep(existingControlResult);
    return { name: getStepAssigneeOrSystemName(step, lookupContext), role };
}

interface CellControlProps {
    activity: ControlActivity;
}

function CellControl({ activity }: CellControlProps): JSX.Element {
    const { title, controlProgramName } = activity;

    return (
        <span className={theme.cellControl}>
            <div className={theme.cellControlName}>{title}</div>
            <div className={theme.cellControlProgramName}>
                {controlProgramName}
            </div>
        </span>
    );
}

interface CellActorProps {
    actor: Actor;
}

function CellActor({ actor }: CellActorProps) {
    const { t } = useTranslation();
    const { name, role } = actor;

    return (
        <span>
            <div className={theme.cellActorName}>
                {localizeActorName(name, t)}
            </div>
            <div className={theme.cellActorRole}>
                {localizeActorRole(role, t)}
            </div>
        </span>
    );
}

function localizeActorRole(role: ActorRole, t: TFunction): string {
    switch (role) {
        case "Responsible":
            return t("DashboardControlActivities_ActorRole_Responsible");
        case "Reviewer":
            return t("DashboardControlActivities_ActorRole_Reviewer");
        default:
            return assertNever(role);
    }
}

interface CellEventProps {
    event: Event;
    timezone: IanaTimezoneView;
}

function CellEvent({ event, timezone }: CellEventProps) {
    const { t } = useTranslation();
    const statusText = localizeEvent(event.eventType, t);
    const timeText = buildTimeText(event, timezone, t);
    const dotClass = statusDotClassForEventType(event.eventType);

    return (
        <div className={theme.cellEvent}>
            <div className={theme.cellEventStatusDotContainer}>
                <span className={dotClass} />
            </div>
            <div className={theme.cellEventContent}>
                <div className={theme.cellEventLabel}>{statusText}</div>
                <div className={theme.cellEventTime}>{timeText}</div>
            </div>
        </div>
    );
}

const statusDotClass = cva([theme.cellEventStatusDot], {
    variants: {
        status: {
            completed: theme.cellEventStatusDotCompleted,
            completedAfterDue: theme.cellEventStatusDotCompletedAfterDueDate,
            pending: theme.cellEventStatusDotPending,
            overdue: theme.cellEventStatusDotOverdue,
        },
    },
});

function statusDotClassForEventType(eventType: EventType): string {
    const status = convertEventTypeToExistingControlActivityStatus(eventType);
    switch (status) {
        case "Completed":
            return statusDotClass({ status: "completed" });
        case "CompletedAfterDueDate":
            return statusDotClass({ status: "completedAfterDue" });
        case "Pending":
            return statusDotClass({ status: "pending" });
        case "Overdue":
            return statusDotClass({ status: "overdue" });
        default:
            return assertNever(status);
    }
}

type TimePrefix = "Since" | "On";

function eventTypeToTimePrefix(eventType: EventType): TimePrefix {
    switch (eventType) {
        case "Completed":
        case "CompletedAfterDueDate":
            return "On";
        case "PendingOnResponsible":
        case "PendingOnFirstReviewer":
        case "PendingOnSecondReviewer":
        case "OverdueOnResponsible":
        case "OverdueOnFirstReviewer":
        case "OverdueOnSecondReviewer":
            return "Since";
        default:
            return assertNever(eventType);
    }
}

function localizeTimePrefix(timePrefix: TimePrefix, t: TFunction): string {
    switch (timePrefix) {
        case "Since":
            return t("DashboardControlActivities_Event_TimeSince");
        case "On":
            return t("DashboardControlActivities_Event_TimeOn");
        default:
            return assertNever(timePrefix);
    }
}

function buildTimeText(
    event: Event,
    timezone: IanaTimezoneView,
    t: TFunction
): string {
    const timePrefix = eventTypeToTimePrefix(event.eventType);
    const sinceOrOn = localizeTimePrefix(timePrefix, t);
    const at = t("DashboardControlActivities_Event_TimeAt");

    const datetime = event.datetime;
    const date = formatDateWithTimezone(datetime, timezone);
    const time = formatTimeAndTimezone(datetime, timezone);

    return `${sinceOrOn} ${date} ${at} ${time}`;
}

function buildRowLinkUrl(row: Row<ControlActivity>): string {
    const controlResultId = row.original.controlResultId;
    return `${LegacyRoute.CONTROL_RESULT}/${controlResultId}`;
}
